import React, { ComponentProps, ElementType, MouseEvent, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { Fade, Popper, Tooltip, Typography } from '@mui/material'
import { useIsSnapshotMode } from '../../../../shared/hooks/useIsSnapshotMode'
import { useIsRoadmapReadonly } from '../../../../shared/hooks/useIsRoadmapReadonly'
import SvgIcon from '@mui/material/SvgIcon'
import { ReactComponent as ActionsIcon } from '../../../../assets/images/actions/actions.svg'
import { ReactComponent as CloseIcon } from '../../../../assets/images/Close.svg'
import { ReactComponent as TransferIcon } from '../../../../assets/images/actions/transfer.svg'
import { ReactComponent as ReorderModeIcon } from '../../../../assets/images/actions/reorder-mode.svg'
import { ValueMessage } from '../../../ValueNarrativesView/queries/useGetValueNarrativesQuery'
import CircularProgress from '@mui/material/CircularProgress'
import { useTransferValueMessageModalContext } from '../../providers/TransferValueMessageModalProvider'
import { useHistory } from 'react-router-dom'

type ActionId = 'reorder' | 'transfer' | 'close'
type Action = {
    id: ActionId
    icon: ElementType
    name: string
    tooltip?: string
    handler?: () => void | Promise<void>
    loading?: boolean
}

type ValueMessageActionsProps = Pick<ComponentProps<typeof SvgIcon>, 'sx'> & {
    valueMessages: ValueMessage[]
    isReorderModeActive: boolean
    hasReorderChanges: boolean
    onEnterReorderMode: () => void
    onSaveReorder: () => void | Promise<void>
    isTransferSelectionActive: boolean
    onEnterTransferSelection: () => void
    onResetActions: () => void
}

const ValueMessageActions = ({
    valueMessages,
    isReorderModeActive,
    hasReorderChanges,
    onEnterReorderMode,
    onSaveReorder,
    isTransferSelectionActive,
    onEnterTransferSelection,
    onResetActions,
    sx,
}: ValueMessageActionsProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const transferValueMessageModal = useTransferValueMessageModalContext()

    const isSnapshotMode = useIsSnapshotMode()
    const isReadonlyMode = useIsRoadmapReadonly()

    if (isSnapshotMode || isReadonlyMode) {
        return null
    }

    const handleTransferClick = () => {
        if (isTransferSelectionActive) {
            transferValueMessageModal.handleOpen(valueMessages)
            setAnchorEl(null)
            onResetActions()
        } else {
            onEnterTransferSelection()
        }
    }

    const handleReorderClick = async () => {
        if (isReorderModeActive) {
            await onSaveReorder()
            setAnchorEl(null)
        } else {
            onEnterReorderMode()
        }
    }

    const handleClose = () => {
        if (isLoading) return

        setAnchorEl(null)
        onResetActions()
    }

    const actions: Action[] = [
        ...(isTransferSelectionActive
            ? []
            : [
                  {
                      id: 'reorder' as const,
                      name: isReorderModeActive && hasReorderChanges ? 'Save' : '',
                      icon: ReorderModeIcon,
                      tooltip: isReorderModeActive ? 'Save order' : 'Reorder',
                      handler: () => handleReorderClick(),
                  },
              ]),
        ...(isReorderModeActive
            ? []
            : [
                  {
                      id: 'transfer' as const,
                      name: isTransferSelectionActive && valueMessages.length > 0 ? 'Transfer' : '',
                      icon: TransferIcon,
                      tooltip: isTransferSelectionActive ? 'Confirm transfer' : 'Transfer',
                      handler: () => handleTransferClick(),
                  },
              ]),
        {
            id: 'close',
            name: '',
            icon: CloseIcon,
            handler: () => handleClose(),
        },
    ]

    const isLoading = actions.some((action) => !!action.loading)

    const handleToggleMenu = (event: MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            setAnchorEl(null)
            onResetActions()
        } else {
            setAnchorEl(event.currentTarget)
        }
    }

    const renderActionRow = (action: Action) => {
        const isActiveReorderAction = action.id === 'reorder' && isReorderModeActive
        const isActiveTransferAction = action.id === 'transfer' && isTransferSelectionActive

        const handleOnClick = async (event: MouseEvent<HTMLElement>) => {
            if (isLoading) return

            await action.handler?.()
        }

        const actionRow = (
            <Stack
                onClick={handleOnClick}
                direction="row"
                px={1}
                py={0.75}
                alignItems="center"
                sx={{
                    color:
                        action.loading || isActiveReorderAction || isActiveTransferAction
                            ? 'figma.primary'
                            : 'figma.grayscale.textHier2',
                    borderRadius: '999px',
                    '&:hover': {
                        color: 'figma.primary',
                    },
                    opacity: isLoading && !action.loading ? 0.4 : 1,
                    cursor: isLoading ? (action.loading ? 'progress' : 'not-allowed') : 'pointer',
                }}
            >
                <SvgIcon
                    component={action.loading ? () => <CircularProgress size={24} color="primary" /> : action.icon}
                    inheritViewBox
                    fontSize="small"
                    color="inherit"
                    sx={{ fontSize: '18px' }}
                />
                <Typography
                    variant="14x400"
                    color="inherit"
                    whiteSpace="nowrap"
                    ml={action.name ? 0.5 : 0}
                    sx={{ textTransform: 'uppercase' }}
                >
                    {action.name}
                </Typography>
            </Stack>
        )

        return action.tooltip ? (
            <Tooltip title={action.tooltip} arrow>
                {actionRow}
            </Tooltip>
        ) : (
            actionRow
        )
    }

    return (
        <Stack>
            <IconButton
                onClick={handleToggleMenu}
                size="small"
                sx={{ p: 0.5, color: !!anchorEl ? 'figma.primary' : 'figma.grayscale.tier2' }}
            >
                <SvgIcon
                    component={ActionsIcon}
                    inheritViewBox
                    color={'inherit'}
                    sx={[{ fontSize: '1.8rem' }, ...(Array.isArray(sx) ? sx : [sx])]}
                />
            </IconButton>
            <Popper
                disablePortal
                open={!!anchorEl}
                anchorEl={anchorEl}
                placement="right"
                sx={(theme) => ({ zIndex: theme.zIndex.drawer })}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} in={!!anchorEl} timeout={800} unmountOnExit mountOnEnter>
                        <Stack
                            direction="row"
                            py={0.75}
                            boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                            borderRadius="12px"
                            bgcolor={
                                isReorderModeActive || isTransferSelectionActive
                                    ? 'figma.primary2'
                                    : 'figma.grayscale.tier7'
                            }
                            ml={1}
                        >
                            {actions.map((action) => {
                                return renderActionRow(action)
                            })}
                        </Stack>
                    </Fade>
                )}
            </Popper>
        </Stack>
    )
}

export default ValueMessageActions
